import { Group, Stack } from "@mantine/core"
import { ExportButton, ExportType, Select, DateInput } from "@tfg/ui"
import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { UndeliveredEventsSchema } from "@tfg/types/schemas/enum"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { BouncedEmailsFilters } from "~/pages/Admin/Exports/BouncedEmails/BouncedEmailsContainer"
import { useGetExportColumns } from "~/pages/Admin/Exports/BouncedEmails/export-columns"
import { useExportBouncedEmails } from "~/pages/Admin/Exports/BouncedEmails/hooks/useExportBouncedEmails"

type Props = {
  bouncedEmails: AdminResponses.FindAllBouncedEmailsResponseDto
  filters: BouncedEmailsFilters
  setFilters: Dispatch<SetStateAction<BouncedEmailsFilters>>
  setPagination: Dispatch<
    SetStateAction<{ pageIndex: number; pageSize: number }>
  >
  pagination: { pageIndex: number; pageSize: number }
}

export const BouncedEmailsTable = ({
  bouncedEmails,
  filters,
  setFilters,
  setPagination,
  pagination,
}: Props) => {
  const columns = useGetExportColumns()
  const { mutate: csvDownload, isPending: isExportingBouncedEmails } =
    useExportBouncedEmails({
      filters,
    })
  const { t } = useTranslation(["products", "common"])
  const table = useCustomReactTable({
    data: bouncedEmails.bouncedEmails,
    columns,
    state: {
      pagination,
    },
    rowCount: bouncedEmails.total,
    onPaginationChange: setPagination,
    manualPagination: true,
  })

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <Select
            data={bouncedEmails.events}
            label="Event"
            placeholder="Select event"
            searchable
            clearable
            size="xs"
            value={filters.event}
            onChange={(event) => {
              const eventType = event as z.infer<typeof UndeliveredEventsSchema>
              setFilters({ ...filters, event: eventType })
            }}
          />
          <Select
            data={bouncedEmails.organizations.map((organization) => ({
              value: organization.id,
              label: organization.name,
            }))}
            label="Inviter Company"
            placeholder="Select event"
            searchable
            clearable
            value={filters.inviterOrganizationId}
            size="xs"
            onChange={(inviterOrganizationId) => {
              setFilters({ ...filters, inviterOrganizationId })
            }}
          />
          <Select
            data={bouncedEmails.suppliers}
            label="Supplier Email"
            placeholder="Select event"
            searchable
            clearable
            size="xs"
            value={filters.supplierEmail}
            onChange={(supplierEmail) =>
              setFilters({ ...filters, supplierEmail })
            }
          />
          <DateInput
            miw={150}
            clearable
            size="xs"
            label="From date"
            value={filters.date}
            placeholder="Select date"
            onChange={(date) => {
              setFilters({ ...filters, date: date ? new Date(date) : null })
            }}
          />
        </Group>
        <Group>
          <ExportButton
            label={t("lists.export.all")}
            exportOptions={[
              {
                type: ExportType.CSV,
                label: t("common:actionButtons.exportAsCsv"),
                exportFunction: csvDownload,
                isLoading: isExportingBouncedEmails,
              },
            ]}
          />
        </Group>
      </Group>
      <CustomTable
        table={table}
        manualPaginationCount={bouncedEmails.total || 0}
      />
    </Stack>
  )
}
